/**
 * 원격지원 기록 표시 문구 — 관리자 콘솔 이력과 직원 본인의 '원격지원 기록'이 공유한다.
 * 값의 출처는 서버 remote_support_sessions.end_reason / ended_by 다.
 */

const END_REASON_LABELS: Record<string, string> = {
  normal: '정상 종료',
  disconnect: '연결 끊김',
  timeout: '시간 초과',
  server_restart: '서버 재시작',
  policy_off: '관리자가 기능을 끔',
  operator_revoked: '담당자 권한 해제',
  share_ended: '화면 공유 중지',
  picker_canceled: '화면 공유 취소',
};

const ENDED_BY_LABELS: Record<string, string> = {
  host: '직원',
  operator: '담당자',
  system: '시스템',
};

export function endReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '진행 중';
  return END_REASON_LABELS[reason] ?? reason;
}

export function endedByLabel(endedBy: string | null | undefined): string {
  if (!endedBy) return '';
  return ENDED_BY_LABELS[endedBy] ?? endedBy;
}

/** 초 → "12분 34초" / "1시간 2분" / "45초" */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '-';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
  if (m > 0) return sec > 0 ? `${m}분 ${sec}초` : `${m}분`;
  return `${sec}초`;
}

/**
 * 주고받은 파일 수 — 보는 사람 기준으로 말한다. 0이면 빈 문자열(행을 감춘다)
 * @param toHost 담당자 → 직원 PC / @param toOperator 직원 PC → 담당자
 * @param viewer 'host' = 직원(받은 = toHost) / 'operator' = 담당자(보낸 = toHost) / 'admin' = 방향 이름으로
 */
export function filesLabel(
  toHost: number | null | undefined,
  toOperator: number | null | undefined,
  viewer: 'host' | 'operator' | 'admin'
): string {
  const a = Math.max(0, Number(toHost) || 0);
  const b = Math.max(0, Number(toOperator) || 0);
  if (a === 0 && b === 0) return '';
  const parts: string[] = [];
  if (viewer === 'admin') {
    if (a > 0) parts.push(`담당자→직원 ${a}개`);
    if (b > 0) parts.push(`직원→담당자 ${b}개`);
  } else {
    const [recv, sent] = viewer === 'host' ? [a, b] : [b, a];
    if (recv > 0) parts.push(`받은 파일 ${recv}개`);
    if (sent > 0) parts.push(`보낸 파일 ${sent}개`);
  }
  return parts.join(' · ');
}

/** 기록 목록용 짧은 날짜·시각 — "10. 7. 14:03" */
export function formatSessionTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}
